import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MdCallEnd,
  MdArrowForward,
  MdChatBubbleOutline,
  MdExpandLess,
  MdOutlineMic,
  MdOutlineMicOff,
  MdPhone,
  MdPhoneInTalk,
  MdVolumeUp,
  MdRemove
} from 'react-icons/md'
import styled, { css, keyframes } from 'styled-components'
import { ChatCallPermission } from '@/models/chat.model'
import { normalizedVoiceContactName } from './contactName'
import { VoiceChannel, VoiceDirection } from './voiceApi'

export type CallView = {
  name: string
  phone: string
  photo?: string
  channel: VoiceChannel
  direction: VoiceDirection
  stageName?: string
  ticket?: string
  callPermission?: ChatCallPermission
  chatIsOpen?: boolean
}

export type QueuedCallView = CallView & { id: string }

type WaitingCallsProps = {
  calls: QueuedCallView[]
  busy: boolean
  onAccept: (callId: string) => void
  onReject: (callId: string) => void
  onOpenChat: (callId: string) => void
  compact?: boolean
}

export type CallDisplayStatus = 'preparing' | 'calling' | 'connected' | 'stabilizing'

const WIDGET_POSITION_KEY = 'devzapp.voice.widget-position.v1'

function storedWidgetPosition() {
  try {
    const value = JSON.parse(sessionStorage.getItem(WIDGET_POSITION_KEY) || 'null')
    return typeof value?.x === 'number' && typeof value?.y === 'number' ? value : null
  } catch {
    return null
  }
}

const ring = keyframes`
  0%, 100% { box-shadow: 0 0 0 2px rgba(0, 168, 132, .2); }
  50% { box-shadow: 0 0 0 7px rgba(0, 168, 132, .08); }
`

const Surface = styled.aside<{ $speaking?: boolean }>`
  position: fixed;
  z-index: ${({ theme }) => theme.layers.alwaysOnTop + 1000};
  overflow: hidden;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.background.secondary};
  border: 1px solid
    ${({ $speaking, theme }) => ($speaking ? theme.colors.accentColor : theme.colors.borderColorPrimary)};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  transition: border-color 160ms ease, box-shadow 160ms ease;

  ${({ $speaking, theme }) =>
    $speaking &&
    css`
      box-shadow: 0 0 0 2px ${theme.colors.accentColor}2e, ${theme.shadows.xl};
    `}
`

const IncomingShell = styled(Surface)`
  top: 140px;
  right: 28px;
  width: min(418px, calc(100vw - 32px));
  padding: 18px;
  border-radius: 18px;

  @media (max-width: 768px) {
    top: auto;
    right: 12px;
    bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  }
`

const ActiveShell = styled(Surface)<{
  $minimized: boolean
  $position: { x: number; y: number } | null
}>`
  position: fixed;
  top: 112px;
  right: 24px;
  bottom: auto;
  width: ${({ $minimized }) => ($minimized ? 'min(620px, calc(100vw - 24px))' : 'min(430px, calc(100vw - 32px))')};
  border-radius: ${({ $minimized }) => ($minimized ? '999px' : '22px')};

  ${({ $minimized, $position }) =>
    $minimized &&
    $position &&
    css`
      top: ${$position.y}px;
      left: ${$position.x}px;
      right: auto;
      bottom: auto;
    `}

  @media (max-width: 768px) {
    ${({ $position }) =>
      !$position &&
      css`
        top: 84px;
        right: 10px;
        bottom: auto;
      `}
  }
`

const PersonRow = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 12px;
`

const Avatar = styled.div<{ $incoming?: boolean }>`
  position: relative;
  width: 54px;
  height: 54px;
  flex: 0 0 54px;
  display: grid;
  place-items: center;
  overflow: visible;
  border-radius: 50%;
  color: #fff;
  background: #a84156;
  font-size: 16px;
  font-weight: 700;
  ${({ $incoming }) =>
    $incoming &&
    css`
      animation: ${ring} 1.5s ease-in-out infinite;
    `}

  img {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: inherit;
  }

  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 1px;
    width: 10px;
    height: 10px;
    border: 2px solid ${({ theme }) => theme.background.secondary};
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.accentColor};
    z-index: 3;
  }
`

const AvatarForeground = styled.span`
  position: relative;
  z-index: 1;
  display: contents;
`

const Halo = styled.span`
  position: absolute;
  inset: -8px;
  z-index: 0;
  pointer-events: none;
`

const haloShadow = ({ $energy, theme }: { $energy: number; theme: any }) =>
  `0 0 ${7 + $energy * 9}px ${theme.colors.accentColor}66`

const haloBarTransform = ({ $index, $bars }: { $index: number; $bars: number }) =>
  `translate(-50%, -50%) rotate(${(360 / $bars) * $index}deg) translateY(-34px)`

const HaloRing = styled.span<{ $energy: number; $speaking: boolean }>`
  position: absolute;
  inset: 6px;
  border: 2px solid ${({ theme }) => theme.colors.accentColor};
  border-radius: 50%;
  opacity: ${({ $speaking }) => ($speaking ? 0.85 : 0)};
  transform: scale(${({ $energy }) => 1 + $energy * 0.03});
  box-shadow: ${haloShadow};
  transition: opacity 180ms ease-out, transform 180ms ease-out, box-shadow 180ms ease-out;
`

const HaloBar = styled.span<{ $index: number; $bars: number; $energy: number; $speaking: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 2px;
  height: ${({ $energy, $index }) => 3 + $energy * 6 * (0.72 + Math.sin($index * 0.86) * 0.14)}px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.accentColor};
  opacity: ${({ $speaking, $energy }) => ($speaking ? 0.48 + $energy * 0.34 : 0)};
  transform: ${haloBarTransform};
  transition: height 150ms ease-out, opacity 180ms ease-out;
`

const Details = styled.div`
  min-width: 0;
  flex: 1;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    font-size: 17px;
    line-height: 1.3;
  }

  span {
    margin-top: 2px;
    color: ${({ theme }) => theme.colors.text.tertiary};
    font-size: 13px;
  }
`

const Queue = styled.section<{ $compact?: boolean }>`
  margin-top: 14px;
  padding-top: 13px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderColorPrimary};
`

const QueueTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 9px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;

  span {
    min-width: 18px;
    height: 18px;
    display: inline-grid;
    place-items: center;
    border-radius: 999px;
    color: ${({ theme }) => theme.background.primary};
    background: ${({ theme }) => theme.colors.warning};
    font-size: 10px;
  }
`

const QueueList = styled.div`
  display: grid;
  gap: 8px;
`

const QueueItem = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 11px;
  background: ${({ theme }) => theme.background.input};

  ${({ $compact }) =>
    $compact &&
    css`
      display: grid;
      grid-template-columns: 38px minmax(0, 1fr);

      > div:last-child {
        grid-column: 1 / -1;
      }
    `}

  ${Avatar} {
    width: 38px;
    height: 38px;
    flex-basis: 38px;
    font-size: 12px;
  }

  ${Details} strong {
    font-size: 13px;
  }

  ${Details} span {
    color: ${({ theme }) => theme.colors.warning};
    font-size: 11px;
    font-weight: 600;
  }
`

const QueueActions = styled.div<{ $compact?: boolean }>`
  display: flex;
  justify-content: flex-end;
  gap: 6px;

  button {
    width: ${({ $compact }) => ($compact ? 'auto' : '34px')};
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${({ $compact }) => ($compact ? '5px' : '0')};
    padding: ${({ $compact }) => ($compact ? '0 10px' : '0')};
    border: 1px solid ${({ theme }) => theme.colors.borderColorPrimary};
    border-radius: ${({ $compact }) => ($compact ? '8px' : '50%')};
    color: ${({ theme }) => theme.colors.text.secondary};
    background: transparent;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;

    &[data-action='reject'] {
      border-color: ${({ theme }) => theme.colors.danger};
      color: ${({ theme }) => theme.colors.danger};
    }

    &[data-action='accept'] {
      border-color: ${({ theme }) => theme.colors.accentColor};
      color: ${({ theme }) => theme.background.primary};
      background: ${({ theme }) => theme.colors.accentColor};
    }

    &:disabled {
      cursor: wait;
      opacity: 0.5;
    }
  }
`

const IncomingLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  color: ${({ theme }) => theme.colors.accentColor};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const LinkedChat = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${({ theme }) => theme.background.input};
`

const LinkedChatRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const LinkedChatDetails = styled.div`
  min-width: 0;

  small,
  span {
    display: block;
  }

  small {
    color: ${({ theme }) => theme.colors.text.tertiary};
    font-size: 12px;
  }

  span {
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
  }
`

const LinkButton = styled.button`
  flex: 0 0 auto;
  padding: 6px;
  border: 0;
  color: ${({ theme }) => theme.colors.accentColor};
  background: transparent;
  cursor: pointer;
  font-weight: 600;
`

const Actions = styled.div`
  display: flex;
  gap: 9px;
  margin-top: 14px;
`

const RingtoneAlert = styled.div`
  margin-top: 14px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.warning};
  border-radius: 11px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.background.input};
  font-size: 13px;

  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 7px;
    padding: 0;
    border: 0;
    color: ${({ theme }) => theme.colors.accentColor};
    background: transparent;
    cursor: pointer;
    font: inherit;
    font-weight: 700;
  }
`

const ActionButton = styled.button<{
  $danger?: boolean
  $primary?: boolean
  $compact?: boolean
  $warning?: boolean
}>`
  min-width: ${({ $compact }) => ($compact ? '44px' : '132px')};
  height: 44px;
  flex: ${({ $compact }) => ($compact ? '0 0 44px' : '1')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 16px;
  border: 1px solid
    ${({ $danger, $primary, $warning, theme }) =>
      $danger
        ? theme.colors.danger
        : $primary
        ? theme.colors.accentColor
        : $warning
        ? theme.colors.warning
        : theme.colors.borderColorPrimary};
  border-radius: 11px;
  color: ${({ $danger, $primary, $warning, theme }) =>
    $danger || $primary ? '#fff' : $warning ? theme.colors.warning : theme.colors.text.secondary};
  background: ${({ $danger, $primary, $warning, theme }) =>
    $danger
      ? theme.colors.danger
      : $primary
      ? theme.colors.accentColor
      : $warning
      ? `${theme.colors.warning}1f`
      : theme.background.secondary};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: filter 140ms ease, transform 140ms ease;

  &:hover:not(:disabled) {
    filter: brightness(1.06);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.55;
  }

  > svg {
    width: 21px;
    height: 21px;
    flex: 0 0 21px;
  }
`

const ActionLabel = styled.span`
  @media (max-width: 640px) {
    display: none;
  }
`

const ActiveContent = styled.div`
  display: grid;
  gap: 18px;
  padding: 20px;

  ${PersonRow} {
    align-items: flex-start;
  }

  > ${PersonRow} ${Avatar} {
    width: 64px;
    height: 64px;
    flex-basis: 64px;
    font-size: 18px;
  }

  > ${PersonRow} ${Details} strong {
    font-size: 20px;
  }

  > ${Actions} {
    margin-top: 0;
    gap: 10px;
  }

  @media (max-width: 640px) {
    gap: 14px;
    padding: 16px;

    ${Actions} {
      ${ActionButton} {
        min-width: 0;
        padding: 0 12px;
      }
    }
  }
`

const ActiveExpanded = styled.div`
  ${Queue} {
    margin: 0 20px 20px;
  }
`

const ActiveHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: ${({ theme }) => theme.colors.accentColor};

  strong {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    letter-spacing: 0.045em;
    text-transform: uppercase;
  }
`

const MinimizeButton = styled.button`
  width: 34px;
  height: 30px;
  display: grid;
  place-items: center;
  flex: 0 0 34px;
  border: 0;
  border-radius: 9px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  background: transparent;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.background.input};
  }
`

const Status = styled.span`
  color: ${({ theme }) => theme.colors.accentColor} !important;
  font-weight: 600;
`

const MiniBar = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 9px;
  padding: 8px 10px;
  color: inherit;
  background: transparent;

  ${Avatar} {
    width: 42px;
    height: 42px;
    flex-basis: 42px;
    font-size: 13px;
  }

  ${Details} {
    flex: 1;
  }

  ${Details} > span:last-child {
    display: none;
  }

  ${ActionButton} {
    min-width: 42px;
    width: 42px;
    height: 42px;
    flex: 0 0 42px;
    padding: 0;
    border-radius: 50%;
  }

  ${ActionLabel} {
    display: none;
  }

  @media (max-width: 640px) {
    gap: 6px;

    > [data-waiting-badge] {
      display: none;
    }
  }
`

const CompactIdentity = styled.button`
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  border: 0;
  color: inherit;
  background: transparent;
  cursor: grab;
  text-align: left;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }

  ${Details} {
    pointer-events: none;
  }
`

const WaitingBadge = styled.span`
  flex: 0 0 auto;
  padding: 5px 8px;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.warning};
  background: ${({ theme }) => theme.colors.warning}18;
  font-size: 11px;
  font-weight: 700;
`

const CompactExpand = styled.button`
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.text.tertiary};
  background: transparent;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.background.input};
  }
`

function initials(name: string, phone: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return phone.slice(-2) || 'WA'
  return `${parts[0][0]}${parts.length > 1 ? parts.at(-1)?.[0] : ''}`.toUpperCase()
}

function AvatarContent({ call }: { call: CallView }) {
  return (
    <AvatarForeground>
      {call.photo ? <img src={call.photo} alt="" /> : <>{initials(call.name, call.phone)}</>}
    </AvatarForeground>
  )
}

function VoiceActivityHalo({ level, speaking }: { level: number; speaking: boolean }) {
  const normalized = Math.min(1, Math.max(0, (level - 0.006) / 0.12))
  const energy = speaking ? Math.max(0.12, normalized) : 0
  const bars = 28
  return (
    <Halo aria-hidden="true">
      <HaloRing $energy={energy} $speaking={speaking} />
      {Array.from({ length: bars }, (_, index) => (
        <HaloBar key={index} $index={index} $bars={bars} $energy={energy} $speaking={speaking} />
      ))}
    </Halo>
  )
}

function ActiveAvatar({ call, speaking, audioLevel }: { call: CallView; speaking: boolean; audioLevel: number }) {
  return (
    <Avatar>
      <VoiceActivityHalo level={audioLevel} speaking={speaking} />
      <AvatarContent call={call} />
    </Avatar>
  )
}

function WaitingCalls({ calls, busy, onAccept, onReject, onOpenChat, compact = false }: WaitingCallsProps) {
  const { t } = useTranslation()
  if (!calls.length) return null
  return (
    <Queue $compact={compact} aria-label={t('translation:voice.waitingCalls')}>
      <QueueTitle>
        {t('translation:voice.waiting')} <span>{calls.length}</span>
      </QueueTitle>
      <QueueList>
        {calls.map(call => {
          const displayName = normalizedVoiceContactName(call.name, call.phone) || t('translation:voice.unnamedContact')
          const openConversationLabel = call.name
            ? t('translation:voice.openConversationOf', 'Abrir conversa de {{name}}', { name: displayName })
            : t('translation:voice.openConversation')
          return (
            <QueueItem key={call.id} $compact={compact}>
              <Avatar>
                <AvatarContent call={call} />
              </Avatar>
              <Details>
                <strong>{displayName}</strong>
                <span>{t('translation:voice.waitingForAnswer')}</span>
              </Details>
              <QueueActions $compact={compact}>
                {!call.chatIsOpen && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onOpenChat(call.id)}
                    title={openConversationLabel}
                  >
                    {compact ? (
                      <>
                        <span>{openConversationLabel}</span>
                        <MdArrowForward size={15} />
                      </>
                    ) : (
                      <MdChatBubbleOutline size={18} />
                    )}
                  </button>
                )}
                <button
                  type="button"
                  data-action="reject"
                  disabled={busy}
                  onClick={() => onReject(call.id)}
                  title={t('translation:voice.reject')}
                >
                  <MdCallEnd size={18} />
                  {compact && <span>{t('translation:voice.reject')}</span>}
                </button>
                <button
                  type="button"
                  data-action="accept"
                  disabled={busy}
                  onClick={() => onAccept(call.id)}
                  title={t('translation:voice.answer')}
                >
                  <MdPhone size={18} />
                  {compact && <span>{t('translation:voice.answer')}</span>}
                </button>
              </QueueActions>
            </QueueItem>
          )
        })}
      </QueueList>
    </Queue>
  )
}

export function IncomingCallCard({
  call,
  ringtoneError,
  onRetryRingtone,
  waitingCalls,
  busy,
  onAccept,
  onReject,
  onOpenChat,
  onAcceptWaiting,
  onRejectWaiting,
  onOpenWaitingChat
}: {
  call: QueuedCallView
  ringtoneError?: 'blocked' | 'failed' | null
  onRetryRingtone?: () => void
  waitingCalls: QueuedCallView[]
  busy: boolean
  onAccept: () => void
  onReject: () => void
  onOpenChat?: () => void
  onAcceptWaiting: (callId: string) => void
  onRejectWaiting: (callId: string) => void
  onOpenWaitingChat: (callId: string) => void
}) {
  const { t } = useTranslation()
  const displayName = normalizedVoiceContactName(call.name, call.phone) || t('translation:voice.unnamedContact')
  const openConversationLabel = call.name
    ? t('translation:voice.openConversationOf', 'Abrir conversa de {{name}}', { name: displayName })
    : t('translation:voice.openConversation')
  return (
    <IncomingShell role="dialog" aria-label={t('translation:voice.incomingCall')}>
      <PersonRow>
        <Avatar $incoming>
          <AvatarContent call={call} />
        </Avatar>
        <Details>
          <IncomingLabel>
            <MdPhoneInTalk size={16} /> {t('translation:voice.incomingCall')}
          </IncomingLabel>
          <strong>{displayName}</strong>
          <span>
            {call.phone} · {t(`translation:voice.channel.${call.channel}`)}
          </span>
        </Details>
      </PersonRow>

      {(call.stageName || call.ticket || onOpenChat) && (
        <LinkedChat>
          <LinkedChatRow>
            <LinkedChatDetails>
              <small>{t('translation:voice.linkedService')}</small>
              <span>
                {call.ticket
                  ? t('translation:voice.openTicket', 'Ticket em aberto · {{ticket}}', { ticket: call.ticket })
                  : call.stageName || t('translation:voice.openConversation')}
              </span>
            </LinkedChatDetails>
            {onOpenChat && <LinkButton onClick={onOpenChat}>{openConversationLabel}</LinkButton>}
          </LinkedChatRow>
        </LinkedChat>
      )}

      {ringtoneError && onRetryRingtone && (
        <RingtoneAlert role="alert">
          {t(`translation:voice.ringtone.${ringtoneError}`)}
          <br />
          <button type="button" onClick={onRetryRingtone}>
            <MdVolumeUp size={18} /> {t('translation:voice.ringtone.enable')}
          </button>
        </RingtoneAlert>
      )}

      <Actions>
        <ActionButton $danger disabled={busy} onClick={onReject}>
          <MdCallEnd size={20} /> {t('translation:voice.reject')}
        </ActionButton>
        <ActionButton $primary disabled={busy} onClick={onAccept}>
          <MdPhone size={20} /> {t('translation:voice.accept')}
        </ActionButton>
      </Actions>
      <WaitingCalls
        compact
        calls={waitingCalls}
        busy={busy}
        onAccept={onAcceptWaiting}
        onReject={onRejectWaiting}
        onOpenChat={onOpenWaitingChat}
      />
    </IncomingShell>
  )
}

export function ActiveCallCard({
  call,
  waitingCalls = [],
  busy = false,
  elapsed,
  muted,
  speaking,
  audioLevel,
  callStatus,
  onMute,
  onHangup,
  onOpenChat,
  onAcceptWaiting,
  onRejectWaiting,
  onOpenWaitingChat,
  minimizedByDefault = false
}: {
  call: CallView
  waitingCalls?: QueuedCallView[]
  busy?: boolean
  elapsed: string
  muted: boolean
  speaking: boolean
  audioLevel: number
  callStatus: CallDisplayStatus
  onMute: () => void
  onHangup: () => void
  onOpenChat?: () => void
  onAcceptWaiting?: (callId: string) => void
  onRejectWaiting?: (callId: string) => void
  onOpenWaitingChat?: (callId: string) => void
  minimizedByDefault?: boolean
}) {
  const { t } = useTranslation()
  const displayName = normalizedVoiceContactName(call.name, call.phone) || t('translation:voice.unnamedContact')
  const openConversationLabel = call.name
    ? t('translation:voice.openConversationOf', 'Abrir conversa de {{name}}', { name: displayName })
    : t('translation:voice.openConversation')
  const [minimized, setMinimized] = useState(minimizedByDefault)
  const [position, setPosition] = useState<{ x: number; y: number } | null>(storedWidgetPosition)
  const shellRef = useRef<HTMLElement | null>(null)
  const dragRef = useRef<{ pointerId: number; offsetX: number; offsetY: number } | null>(null)
  const status = t(`translation:voice.callStatus.${callStatus}`)
  const secondary = useMemo(
    () => `${call.phone} · ${t(`translation:voice.channel.${call.channel}`)}`,
    [call.channel, call.phone, t]
  )

  const clampPosition = useCallback((x: number, y: number) => {
    const rect = shellRef.current?.getBoundingClientRect()
    const width = rect?.width || Math.min(360, window.innerWidth - 32)
    const height = rect?.height || 64
    const margin = 8
    return {
      x: Math.min(Math.max(x, margin), Math.max(margin, window.innerWidth - width - margin)),
      y: Math.min(Math.max(y, margin), Math.max(margin, window.innerHeight - height - margin))
    }
  }, [])

  const startDragging = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    const rect = shellRef.current?.getBoundingClientRect()
    if (!rect) return
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top
    }
    event.currentTarget.setPointerCapture(event.pointerId)
    event.preventDefault()
  }, [])

  const drag = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const current = dragRef.current
      if (!current || current.pointerId !== event.pointerId) return
      const next = clampPosition(event.clientX - current.offsetX, event.clientY - current.offsetY)
      setPosition(next)
      sessionStorage.setItem(WIDGET_POSITION_KEY, JSON.stringify(next))
    },
    [clampPosition]
  )

  const stopDragging = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return
    dragRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }, [])

  useEffect(() => {
    const keepInsideViewport = () => setPosition(current => (current ? clampPosition(current.x, current.y) : current))
    window.addEventListener('resize', keepInsideViewport)
    return () => window.removeEventListener('resize', keepInsideViewport)
  }, [clampPosition])

  useEffect(() => {
    if (!minimized) return
    const frame = window.requestAnimationFrame(() => {
      setPosition(current => (current ? clampPosition(current.x, current.y) : current))
    })
    return () => window.cancelAnimationFrame(frame)
  }, [clampPosition, minimized, waitingCalls.length])

  return (
    <ActiveShell
      ref={shellRef}
      $speaking={speaking}
      $minimized={minimized}
      $position={minimized ? position : null}
      aria-label={t('translation:voice.activeCall')}
    >
      {minimized ? (
        <MiniBar>
          <CompactIdentity
            type="button"
            onPointerDown={startDragging}
            onPointerMove={drag}
            onPointerUp={stopDragging}
            onPointerCancel={stopDragging}
            title={t('translation:voice.move')}
            aria-label={t('translation:voice.move')}
          >
            <ActiveAvatar call={call} speaking={speaking} audioLevel={audioLevel} />
            <Details>
              <strong>{displayName}</strong>
              <Status>
                {status} · {elapsed}
              </Status>
              <span>{secondary}</span>
            </Details>
          </CompactIdentity>
          {!!waitingCalls.length && (
            <WaitingBadge data-waiting-badge>
              {t('translation:voice.waitingCount', '{{count}} em espera', { count: waitingCalls.length })}
            </WaitingBadge>
          )}
          {onOpenChat && (
            <ActionButton $compact onClick={onOpenChat} title={openConversationLabel}>
              <MdChatBubbleOutline size={20} />
            </ActionButton>
          )}
          <CompactExpand
            type="button"
            onClick={() => setMinimized(false)}
            title={t('translation:voice.maximize')}
            aria-label={t('translation:voice.maximize')}
          >
            <MdExpandLess size={21} />
          </CompactExpand>
          <ActionButton
            $compact
            $warning={muted}
            onClick={onMute}
            title={muted ? t('translation:voice.unmute') : t('translation:voice.mute')}
          >
            {muted ? <MdOutlineMicOff size={21} /> : <MdOutlineMic size={21} />}
            <ActionLabel>{muted ? t('translation:voice.muted') : t('translation:voice.microphone')}</ActionLabel>
          </ActionButton>
          <ActionButton $compact $danger onClick={onHangup} title={t('translation:voice.hangup')}>
            <MdCallEnd size={21} />
            <ActionLabel>{t('translation:voice.hangup')}</ActionLabel>
          </ActionButton>
        </MiniBar>
      ) : (
        <ActiveExpanded>
          <ActiveContent>
            <ActiveHeader>
              <strong>
                <MdPhoneInTalk size={17} /> {t('translation:voice.activeCall')}
              </strong>
              <MinimizeButton
                type="button"
                onClick={() => setMinimized(true)}
                title={t('translation:voice.minimize')}
                aria-label={t('translation:voice.minimize')}
              >
                <MdRemove size={21} />
              </MinimizeButton>
            </ActiveHeader>
            <PersonRow>
              <ActiveAvatar call={call} speaking={speaking} audioLevel={audioLevel} />
              <Details>
                <strong>{displayName}</strong>
                <span>{secondary}</span>
                <Status>
                  {status} · {elapsed}
                </Status>
              </Details>
            </PersonRow>
            <Actions>
              {onOpenChat && (
                <ActionButton $compact onClick={onOpenChat} title={openConversationLabel}>
                  <MdChatBubbleOutline size={22} /> <ActionLabel>{openConversationLabel}</ActionLabel>
                </ActionButton>
              )}
              <ActionButton
                $warning={muted}
                onClick={onMute}
                title={muted ? t('translation:voice.unmute') : t('translation:voice.mute')}
              >
                {muted ? <MdOutlineMicOff size={21} /> : <MdOutlineMic size={21} />}
                <ActionLabel>{muted ? t('translation:voice.muted') : t('translation:voice.microphone')}</ActionLabel>
              </ActionButton>
              <ActionButton $danger onClick={onHangup}>
                <MdCallEnd size={21} /> <ActionLabel>{t('translation:voice.hangup')}</ActionLabel>
              </ActionButton>
            </Actions>
          </ActiveContent>
          {onAcceptWaiting && onRejectWaiting && onOpenWaitingChat && (
            <WaitingCalls
              compact
              calls={waitingCalls}
              busy={busy}
              onAccept={onAcceptWaiting}
              onReject={onRejectWaiting}
              onOpenChat={onOpenWaitingChat}
            />
          )}
        </ActiveExpanded>
      )}
    </ActiveShell>
  )
}
